import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Calendar, Users, Home, FileText } from "lucide-react";
import { api } from "../lib/api";

/**
 * QuickSearch — universal Ctrl+K / Cmd+K spotlight-style command palette.
 * Results are role-aware (backend /api/search). Keyboard: ↑/↓ navigate,
 * Enter opens, ESC closes.
 */
const GROUP_META = {
  bookings:    { label: "Reservas",    icon: Calendar },
  users:       { label: "Pessoas",     icon: Users },
  apartments:  { label: "Alojamentos", icon: Home },
  invoices:    { label: "Faturas",     icon: FileText },
};

export default function QuickSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [data, setData] = useState({ total: 0, groups: {} });
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const go = useNavigate();

  // Keyboard shortcut Ctrl+K / Cmd+K toggles the palette
  useEffect(() => {
    const onKey = e => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(v => !v);
      }
      if (open && e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Autofocus on open + reset state
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQ(""); setData({ total: 0, groups: {} }); setActiveIdx(0);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) { setData({ total: 0, groups: {} }); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
        setData(data);
        setActiveIdx(0);
      } catch { setData({ total: 0, groups: {} }); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  // Flatten for keyboard nav
  const flat = [];
  Object.entries(data.groups || {}).forEach(([gid, items]) => {
    items.forEach(it => flat.push({ ...it, gid }));
  });

  const pick = useCallback(item => {
    if (!item) return;
    setOpen(false);
    go(item.path);
  }, [go]);

  function onInputKey(e) {
    if (!flat.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(flat.length - 1, i + 1)); }
    else if (e.key === "ArrowUp")   { e.preventDefault(); setActiveIdx(i => Math.max(0, i - 1)); }
    else if (e.key === "Enter")     { e.preventDefault(); pick(flat[activeIdx]); }
  }

  return (
    <>
      {/* Trigger button in the header (also shows Ctrl+K hint on desktop) */}
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#1a4640] rounded-sm text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37] transition-colors text-sm"
        aria-label="Pesquisa global"
        data-testid="quick-search-trigger">
        <Search size={14} />
        <span className="hidden md:inline text-xs">Pesquisar…</span>
        <kbd className="hidden md:inline text-[10px] font-mono text-[#a0b2af] bg-[#062c27] px-1.5 py-0.5 rounded border border-[#1a4640]">⌘K</kbd>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-16" data-testid="quick-search-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-2xl bg-[#041f1b] border border-[#1a4640] rounded-sm shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-4 duration-200">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-[#1a4640]">
              <Search size={18} className="text-[#d4af37]" />
              <input
                ref={inputRef}
                value={q}
                onChange={e => setQ(e.target.value)}
                onKeyDown={onInputKey}
                placeholder="Pesquisar reservas, clientes, alojamentos, faturas…"
                className="flex-1 bg-transparent outline-none text-base placeholder:text-[#1a4640]"
                data-testid="quick-search-input" />
              <button onClick={() => setOpen(false)} aria-label="Fechar" className="text-[#a0b2af] hover:text-[#d4af37]">
                <X size={18} />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto" data-testid="quick-search-results">
              {q.trim().length < 2 && (
                <div className="px-5 py-10 text-center text-sm text-[#a0b2af]">
                  Escreva pelo menos 2 caracteres para pesquisar.
                  <div className="mt-3 text-xs opacity-70">
                    Atalho: <kbd className="font-mono px-1.5 py-0.5 border border-[#1a4640] rounded">⌘/Ctrl</kbd> + <kbd className="font-mono px-1.5 py-0.5 border border-[#1a4640] rounded">K</kbd>
                  </div>
                </div>
              )}
              {q.trim().length >= 2 && loading && <div className="px-5 py-6 text-sm text-[#a0b2af]">A pesquisar…</div>}
              {q.trim().length >= 2 && !loading && data.total === 0 && (
                <div className="px-5 py-10 text-center text-sm text-[#a0b2af]">
                  Sem resultados para <span className="text-[#d4af37]">"{q}"</span>.
                </div>
              )}

              {Object.entries(data.groups || {}).map(([gid, items]) => {
                if (!items?.length) return null;
                const meta = GROUP_META[gid] || { label: gid, icon: Search };
                const Ic = meta.icon;
                return (
                  <div key={gid} className="py-2">
                    <div className="px-5 py-1.5 text-[10px] uppercase tracking-widest text-[#a0b2af] bg-[#062c27]/40 flex items-center gap-2">
                      <Ic size={12} /> {meta.label} <span className="opacity-60">· {items.length}</span>
                    </div>
                    {items.map(it => {
                      const idx = flat.findIndex(f => f.gid === gid && f.id === it.id);
                      const active = idx === activeIdx;
                      return (
                        <button
                          key={`${gid}-${it.id}`}
                          onMouseEnter={() => setActiveIdx(idx)}
                          onClick={() => pick(it)}
                          className={`w-full text-left px-5 py-3 flex items-start gap-3 transition-colors ${active ? "bg-[#d4af37]/10 border-l-2 border-[#d4af37]" : "border-l-2 border-transparent hover:bg-[#062c27]"}`}
                          data-testid={`quick-search-item-${gid}-${it.id.slice(0,8)}`}>
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm truncate ${active ? "text-[#d4af37]" : ""}`}>{it.label}</div>
                            {it.sublabel && <div className="text-xs text-[#a0b2af] truncate mt-0.5">{it.sublabel}</div>}
                          </div>
                          {it.status && (
                            <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-[#1a4640] rounded-sm text-[#a0b2af] shrink-0">
                              {it.status}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            <div className="px-5 py-2 border-t border-[#1a4640] flex items-center justify-between text-[10px] text-[#a0b2af]">
              <span>
                <kbd className="font-mono px-1.5 py-0.5 border border-[#1a4640] rounded">↑↓</kbd> navegar ·
                <kbd className="font-mono px-1.5 py-0.5 border border-[#1a4640] rounded ml-1">↵</kbd> abrir ·
                <kbd className="font-mono px-1.5 py-0.5 border border-[#1a4640] rounded ml-1">esc</kbd> fechar
              </span>
              <span className="text-[#d4af37]">{data.total || 0} resultados</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
